import { defaultValue, ICustomer } from 'app/modules/customer/model/customer.model';
import { ACTION_TYPES } from './subscription-actions';

export interface IDocumentMetadata {
  type: string;
}

export interface IDocument {
  uuid: string;
  metadata: IDocumentMetadata;
}

export interface Subscription {
  subscriptionNumber: string;
  legalEndDateReflection: Date;
  loading: boolean;
  createInProgress: boolean;
  errorMessage?: string;
  errorSignMessage?: string;
  offers: string[];
  shortOfferCode: string;
  signTxnCompletedAt: string;
  signTxnUrl: string;
  signTxnId: string;
  status: string;
  externalRef: string;
  source: any;
  documentsSigned: IDocument[];
  frozenAssetStatus: string;
}

export const initialState: Subscription = {
  subscriptionNumber: '',
  legalEndDateReflection: null,
  loading: false,
  createInProgress: false,
  offers: [],
  shortOfferCode: undefined,
  signTxnCompletedAt: null,
  source: undefined as any,
  signTxnUrl: null,
  signTxnId: null,
  status: null,
  documentsSigned: [],
  externalRef: '',
  errorMessage: '',
  errorSignMessage: '',
  frozenAssetStatus: ''
};

export type SubscriptionState = Readonly<Subscription>;

// --- Actions Types

export interface SetSubscription {
  type: typeof ACTION_TYPES.SET_SUBSCRIPTION;
  payload: { subscriptionNumber: string; customer: ICustomer; offers: string[]; signTxnCompletedAt: string };
}

export interface SetInitializeSubscription {
  type: typeof ACTION_TYPES.SET_INITIALIZE_SUBSCRIPTION;
  payload: { offers: string[] };
}

export interface CreatePendingSubscription {
  type: typeof ACTION_TYPES.CREATE_PENDING_SUBSCRIPTION;
  payload: { subscriptionNumber: string };
}

// types/subscription.ts
export interface SubscriptionRequest {
  request: {
    subscriptionChannel: "STORE" | "DIRECT";
    supplier: {
      trackingId: string;
    };
    agreementHolder: {
      agreementHolderType: "PersonRef" | "Person";
      contactInformation: {
        emails: Array<{ email: string }>;
        cellPhones: Array<{ cellPhone: string }>;
        landlineNumbers?: Array<{ landlineNumber: string }>;
        faxNumbers?: Array<{ faxNumber: string }>;
      };
      personName: {
        prefixTitle: { code: string };
        firstName: string;
        lastName: string;
      };
      birthDate: string;
      genderCode: "MALE" | "FEMALE";
      // Champs optionnels pour l'update
      birthCity?: string;
      birthCountry?: { code: string; label: string };
      birthDepartment?: number;
      birthName?: string;
      isThePrincipalInsured?: boolean;
      bankAccount?: {
        accountHolderName: string;
        unstructuredIban: string;
      };
      postalAddress?: {
        buildingLine: string;
        cityLine: string;
        countryLine: { code: string; label: string };
        localityLine: string;
        postalBoxLine: string;
        recipientComplementLine: string;
        recipientNameLine: string;
        streetLine: string;
      };
    };
    includedCoverages: Array<{
      commercialOffer: { code: string };
    }>;
  };
  reCaptchaValue: any;
}

export interface UpdateSubscriptionRequest {
  request: {
    subscriptionChannel: "STORE" | "DIRECT";
    supplier: {
      trackingId: string;
    };
    partyRoleInAgreements: {  // <-- nouveau champ
      agreementHolder: {
        agreementHolderType: "PersonRef" | "Person";
        contactInformation: {
          emails: Array<{ email: string }>;
          cellPhones: Array<{ cellPhone: string }>;
          landlineNumbers?: Array<{ landlineNumber: string }>;
          faxNumbers?: Array<{ faxNumber: string }>;
        };
        personName: {
          prefixTitle: { code: string };
          firstName: string;
          lastName: string;
        };
        birthDate: string;
        genderCode: "MALE" | "FEMALE";
        birthCity?: string;
        birthCountry?: { code: string; label: string };
        birthDepartment?: number;
        birthName?: string;
        isThePrincipalInsured?: boolean;
        bankAccount?: {
          accountHolderName: string;
          unstructuredIban: string;
        };
        customerRef: {};
        postalAddress?: {
          buildingLine: string;
          cityLine: string;
          countryLine: { code: string; label: string };
          localityLine: string;
          postalBoxLine: string;
          recipientComplementLine: string;
          recipientNameLine: string;
          streetLine: string;
        };
      };
    };
  };
}


export interface SubscriptionResponse {
  meta: {
    responseTime: number;
    lang: string;
    apiVersion: string;
  };
  response: {
    subscriptionId: string;
  };
  return: {
    status: string;
  };
  token: string;
}

export interface InsuranceAgreementDetail {
  response: {
    subscriptionChannel: "STORE" | "DIRECT";
    policyId: string;
    policyNumber: string;
    creationDate: string;
    insuranceAgreementStatus: string;
    supplier: {
      store: any;
      trackingId: string;
    };
    relatedPremium: {
      premiumAmount: {
        value: number;
        currency: string;
      };
      paymentMethod: string;
      billingPaymentPlanCode: string;
      billingDay: number;
    };
    effectivePeriod: {
      startDate: string;
      endDate: string;
    };
    insuredManufacturedObjects: any[];
    partyRoleInAgreements: {
      agreementHolder: any;
      principalInsured: any;
      beneficiaries: any;
    };
    includedCoverages: Array<{
      commercialOffer: { code: string; label: string | null };
      coverageOption: { code: string; label: string | null };
      coverageOptionCategory: string;
      coveragesSpecification: any;
    }>;
    documentsInAgreement: any;
    technicalData: any;
  };
}


// --- Create Union Type

export type SubscriptionActions = UpdateSubscriptionRequest | SetSubscription | SetInitializeSubscription | CreatePendingSubscription | InsuranceAgreementDetail | SubscriptionResponse | SubscriptionRequest;
